'use client'
import * as RUIDialog from '@radix-ui/react-dialog'
import { useTranslations } from 'next-intl'
import { createElement, Fragment, useState } from 'react'

import { followUserV2, unfollowUserV2 } from 'api/user'
import Headshot from 'component/block/headshot'
import Link from 'component/block/link'
import LinkifyText from 'component/block/linkify-text'
import { Button } from 'component/ui/button'
import { FOLLOW_STATUS, HeadshotSize } from 'constant'
import { GA_CLICK_ACTION } from 'constant/ga'
import { clientFetch } from 'lib/fetch/client'
import { usePopupStore } from 'store/global/popup'
import { partialUpdateUser, useUserState } from 'store/global/users'
import { useDeviceStore } from 'store/server-context/device'
import { useSessionStore } from 'store/server-context/session'
import type { IImageAssetV2 } from 'type/asset'
import type { ICommunityV2 } from 'type/community'
import type { ValueOf } from 'type/object'
import type { IUserV2WithAsset } from 'type/user'
import { normalizeAsset } from 'util/normalizeAsset'
import { useRouter } from 'lib/next-intl'

type IFollowStatus = ValueOf<typeof FOLLOW_STATUS>

const getFollowTranslation = (followStatus: IFollowStatus) => {
  switch (followStatus) {
    case FOLLOW_STATUS.Followed:
      return 'common.following'
    case FOLLOW_STATUS.Requested:
      return 'common.requested'
    default:
      return 'common.follow'
  }
}

interface ISpotInfo {
  user: IUserV2WithAsset
  caption?: string
  geoName?: string
  geoId?: string
  share: ICommunityV2[]
  commentId: string
}
const SpotInfo = (props: ISpotInfo) => {
  const { user, caption, geoName, share, geoId, commentId } = props
  const showDownloadPopup = usePopupStore(state => state.showDownloadPopup)
  const showAuthPopup = usePopupStore(state => state.showAuthPopup)
  const device = useDeviceStore(state => state.device)
  const t = useTranslations()
  const router = useRouter()
  const session = useSessionStore(state => state.session)

  // sync global user state
  const freshUser = useUserState(user.user_id, {
    follow_status: user.follow_status
  })
  const [showFollow] = useState(() => {
    if (session.guest) {
      return true
    }

    if (user.uid === session.uid) {
      return false
    }

    return freshUser.follow_status !== FOLLOW_STATUS.Followed
  })

  const headshot = user.headshot as IImageAssetV2

  const handleFollowChange = async () => {
    if (session.guest) {
      showAuthPopup()
      return
    }

    let followStatus = FOLLOW_STATUS.None
    if (freshUser.follow_status === FOLLOW_STATUS.None) {
      followStatus = FOLLOW_STATUS.Followed
    }
    partialUpdateUser(user.user_id, { follow_status: followStatus })
    const followApi =
      freshUser.follow_status !== FOLLOW_STATUS.Followed
        ? followUserV2
        : unfollowUserV2

    followApi(clientFetch, {
      user_id: user.user_id
    })
  }

  return (
    <div className='absolute bottom-[12px] left-0 right-[28px] flex translate-x-[16px] transform flex-col space-y-[12px] text-md text-label_still-l1'>
      <div className='flex min-w-0 max-w-[295px] items-center space-x-[8px]'>
        <Link
          href={`/${user.uid}`}
          title={user.uid}
          className='flex min-w-0 items-center space-x-[8px]'
        >
          <Headshot
            {...normalizeAsset(headshot)}
            size={HeadshotSize.sm}
            className='flex-shrink-0'
            gtmTag='post-info-headShot'
            gaAction={GA_CLICK_ACTION.Common.headshotClickAction}
          />
          <div>
            <p
              className='max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap text-callout text-primary-on_primary'
              data-ga-click={GA_CLICK_ACTION.Common.uidClickAction}
            >
              {user.uid}
            </p>
            <p className='max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap text-cation text-label-l2'>
              @{user.name}
            </p>
          </div>
        </Link>
        {showFollow && (
          <Button
            $variant='outline-white'
            $size='xs'
            $radius='md'
            onClick={handleFollowChange}
            className='flex-shrink-0'
          >
            <span>
              {t(
                getFollowTranslation(freshUser.follow_status as IFollowStatus)
              )}
            </span>
          </Button>
        )}
      </div>
      {caption &&
        createElement(
          device !== 'desktop' ? Fragment : RUIDialog.Trigger,
          device !== 'desktop' ? {} : { asChild: true },
          <div
            data-ga-click={GA_CLICK_ACTION.Common.captionClickAction}
            title={caption}
            onClick={() => {
              router.push(`comment/${commentId}`)
            }}
            className='line-clamp-2 max-w-[295px] cursor-pointer break-words font-medium [&>a]:text-label_still-l1'
          >
            <LinkifyText
              text={caption}
              parseHashtag={true}
              parseMention={true}
            />
          </div>
        )}
      {share.length > 0 && (
        <div
          className='flex cursor-pointer items-center space-x-[4px]'
          onClick={showDownloadPopup}
          title={geoName}
          data-ga-click={GA_CLICK_ACTION.Common.locationClickAction}
        >
          <div
            className='i-ps-group_f [--icon-size:16px] shrink-0'
            role='img'
            aria-label='Community'
          />
          <p className='min-w-0 flex items-center shrink'>
            <b className='min-w-0 shrink truncate'>{share[0].title}</b>
            {share.length > 1 && (
              <span className='shrink-0 whitespace-pre'>
                {' '}
                and {share.length - 1} others
              </span>
            )}
          </p>
        </div>
      )}
      <Link
        href={`/place/${geoId}`}
        className='flex cursor-pointer items-center space-x-[4px] max-w-[275px]'
        title={geoName}
        data-ga-click={GA_CLICK_ACTION.Common.locationClickAction}
      >
        <div
          className='i-ps-pin_f [--icon-size:16px] shrink-0'
          role='img'
          aria-label='Location'
        />
        <p className='truncate'>{geoName}</p>
      </Link>
    </div>
  )
}

export default SpotInfo
